import { toast } from '@mindly/ui-components';
import { CreateUserType, UserType } from 'src/pages/types';
import { HttpHandler } from './HttpHandler';
import { environment } from '../enviroments';
import { ApiResponseType } from 'src/shared';

export const getIsExistsSpecialist = async (
  id: string
): Promise<{
  data?: boolean;
  error?: string;
  message?: string;
}> => {
  try {
    const { data } = await HttpHandler({
      method: 'GET',
      url: `${environment.CLIENT_APP_URL}/client/is-exist-specialist`,
    });

    if (data.error) {
      return {
        error: data.error,
        message: data.message,
      };
    }

    return data;
  } catch (error: any) {
    const message = error?.error?.[0]?.msg ?? error.message;
    return {
      error: message,
      message,
    };
  }
};

export const addNewUserPsychologist = async (id?: string, newUserPsychologists?: string): Promise<void> => {
  try {
    await HttpHandler({
      method: 'PATCH',
      url: `${environment.CLIENT_URL}/${id}`,
      data: {
        user_psychologists: newUserPsychologists,
      },
    });
  } catch (error: any) {
    toast(error.message);
  }
};

export const changeUserData = async (object: Partial<UserType>, id: string): Promise<void> => {
  try {
    await HttpHandler({
      method: 'PATCH',
      url: `${environment.CLIENT_URL}/${id}`,
      data: object,
    });
  } catch (error: any) {
    toast(error.message);
  }
};

export const createUser = async (
  userID: string | undefined | null,
  data: CreateUserType,
  lang: string,
  isAnonymous?: boolean
) => {
  if (!userID) throw new Error('user not finded');

  const { data: userData } = await HttpHandler({
    method: 'POST',
    url: isAnonymous
      ? `${environment.CLIENT_URL}/anonymous/${userID}?lang=${lang}`
      : `${environment.CLIENT_URL}/${userID}?lang=${lang}`,
    data: {
      email: data.email,
      name: data.firstName,
      lastName: data.lastName,
      dob: data?.dob ?? null,
      country: data?.country ?? null,
      us_state: data?.us_state ?? null,
      onboarding_key: data?.onBoardingKey ?? null,
      ...data?.attributionParams,
    },
  });
  return userData;
};

export const patchUserAvatar = async (
  userID: string | null | undefined,
  avatar: File,
  onLoad?: (avatar: string) => () => Promise<void>
) => {
  if (!userID) throw new Error('User id not defined');

  const requestBodyFormData = new FormData();
  requestBodyFormData.append('avatar', avatar);

  const XMLRequest = new XMLHttpRequest();

  XMLRequest.open('PATCH', `${environment.CLIENT_URL}/${userID}`);
  XMLRequest.send(requestBodyFormData);

  XMLRequest.onloadend = (await onLoad?.(URL.createObjectURL(avatar))) ?? null;

  return URL.createObjectURL(avatar);
};

export const removeUserApi = async () => {
  try {
    const { data } = await HttpHandler({
      method: 'DELETE',
      url: `${environment.CLIENT_APP_URL}/client/profile/remove`,
    });

    if (data.error) {
      return {
        error: data.error,
        message: data.message,
      };
    }

    return data;
  } catch (error: any) {
    const message = error?.error?.[0]?.msg ?? error.message;
    return {
      error: message,
      message,
    };
  }
};

export const updateUserEmail = async (email: string) => {
  if (!email) throw new Error('Email required');

  try {
    const { data } = await HttpHandler({
      method: 'PATCH',
      url: `${environment.CLIENT_APP_URL}/client/profile/email`,
      data: {
        email,
      },
    });

    if (data.error) {
      return {
        error: data.error,
        message: data.message,
      };
    }

    return data;
  } catch (error: any) {
    const message = error?.error?.[0]?.msg ?? error.message;
    return {
      error: message,
      message,
    };
  }
};

export const updateUserPersonalData = async (
  body: Record<string, any>
): Promise<{
  data?: any;
  error?: string;
  message?: string;
}> => {
  try {
    const { data } = await HttpHandler({
      method: 'PATCH',
      url: `${environment.CLIENT_APP_URL}/client/profile/personal-data`,
      data: body,
    });

    if (data.error) {
      return {
        error: data.error,
        message: data.message,
      };
    }

    return data;
  } catch (error: any) {
    const message = error?.error?.[0]?.msg ?? error.message;
    return {
      error: message,
      message,
    };
  }
};

export const getCustomToken = async (
  id: string
): Promise<{
  token: string;
}> => {
  const { data } = await HttpHandler({
    method: 'GET',
    url: `${environment.CLIENT_URL}/custom-token/${id}`,
  });

  return data;
};

export const getUserById = async (id: string): Promise<UserType | null> => {
  const { data: user } = await HttpHandler({
    method: 'GET',
    url: `${environment.CLIENT_APP_URL}/client/profile`,
  });

  return Object.keys(user)?.length ? user?.data : null;
};

export const linkUserData = async (object: Partial<UserType>, id: string, lang: string): Promise<void> => {
  try {
    await HttpHandler({
      method: 'PATCH',
      url: `${environment.CLIENT_URL}/link/${id}?lang=${lang}`,
      data: object,
    });
  } catch (error: any) {
    toast(error.message);
  }
};

export const createAnonymousUser = async (requestData: {
  app_lang: string;
  onBoardingAnswers?: {};
  onboarding_key?: string;
  is_viewed_specialist_list?: boolean;
}): Promise<ApiResponseType<UserType & { customToken: string }>> => {
  const { data } = await HttpHandler({
    method: 'POST',
    url: `${environment.CLIENT_APP_URL}/client/create/anonymous`,
    headers: {
      'Content-Type': 'application/json',
    },
    data: requestData,
  });

  return data;
};

export const createEmptyClientDocument = async (
  clientId: string,
  requestData: {
    app_lang: string;
    onBoardingAnswers?: {};
    onboarding_key?: string;
    is_viewed_specialist_list?: boolean;
  }
): Promise<ApiResponseType<UserType & { customToken: string }>> => {
  const { data } = await HttpHandler({
    method: 'POST',
    url: `${environment.CLIENT_APP_URL}/client/create/empty/${clientId}`,
    headers: {
      'Content-Type': 'application/json',
    },
    data: requestData,
  });

  return data;
};
